import { useState, MouseEvent } from 'react';
import { Popover } from '@mui/material';

import {
  List,
  ListItem,
  ListItemText,
  ListSearchInput,
  Scroller,
  Skeleton,
  tokens,
  Typography,
  Box,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { NavTriggerCaret } from '../../_components/NavTriggerCaret';
import {
  getNavTriggerButtonSx,
  navTriggerGap,
  navTriggerLabelSx,
  navTriggerPaddingX,
} from '../../_components/navTriggerStyles';

import { HeaderSelectProps } from '../types';

export const HeaderSelect = <T,>({
  config,
  listItemStartAdornmentSx = {
    alignSelf: 'flex-start',
    marginTop: '2px',
  },
  listItemPrimaryTypographyProps = { variant: 'P12M' },
  listSize = 'medium',
  scrollerSx = { gap: '2px', overflow: 'auto', maxHeight: '225px' },
  boxMinWidth = 270,
}: HeaderSelectProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const menuWidth = anchorEl?.getBoundingClientRect().width;

  const { options, isLoading, hasError, refetch } = config;

  const handleOpen = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const closePopover = () => {
    setAnchorEl(null);
    setSearchInputValue('');
  };

  const handleSelect = (event: MouseEvent<HTMLElement>, item: T) => {
    closePopover();
    event.stopPropagation();
    config.onSelect(item);
  };

  const selectedOption = options.find((item) => item.selected);

  const filteredData = options.filter(({ item }) => {
    const searchableTexts = config.getSearchableText(item);
    return searchableTexts.some((text) =>
      (text ?? '').toLowerCase().includes(searchInputValue.toLowerCase())
    );
  });

  const renderListContent = () => {
    if (isLoading) {
      return (
        <Box px={12} py={8}>
          <Skeleton variant="rounded" height={32} />
          <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
          <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Box px={12} py={16}>
          <FailedToLoad title={config.errorTitle} refresh={refetch} compact />
        </Box>
      );
    }

    if (!options.length) {
      return (
        <ListItem
          startAdornment={config.emptyIcon}
          startAdornmentSx={listItemStartAdornmentSx}
          multiline
        >
          <ListItemText
            primary={config.emptyTitle}
            secondary={config.emptySubtitle}
          />
        </ListItem>
      );
    }

    if (!filteredData.length) {
      return (
        <ListItem
          startAdornment={config.emptyIcon}
          startAdornmentSx={listItemStartAdornmentSx}
          multiline
        >
          <ListItemText
            primary={config.notFoundTitle}
            secondary={config.notFoundSubtitle}
          />
        </ListItem>
      );
    }

    return (
      <Scroller sx={scrollerSx}>
        {filteredData.map((option) => (
          <ListItem
            key={config.getItemId(option.item)}
            onClick={(event: MouseEvent<HTMLElement>) =>
              handleSelect(event, option.item)
            }
            selected={option.selected}
            startAdornment={config.renderItemIcon(option.item)}
            startAdornmentSx={listItemStartAdornmentSx}
            multiline
          >
            <ListItemText
              primary={config.getItemName(option.item)}
              primaryTypographyProps={listItemPrimaryTypographyProps}
              secondary={config.getItemSecondaryText?.(option.item)}
            />
          </ListItem>
        ))}
      </Scroller>
    );
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        alignItems="center"
        px={navTriggerPaddingX}
        onClick={handleOpen}
        disabled={isLoading}
        display="flex"
        gap={navTriggerGap}
        sx={getNavTriggerButtonSx(isPopoverOpen)}
        data-testid={`${config.testIdPrefix}-trigger`}
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={120} height={20} />
        ) : selectedOption ? (
          <>
            {config.renderTriggerIcon(selectedOption.item)}
            <Typography
              variant="P14B"
              noWrap
              data-testid={`${config.testIdPrefix}-trigger-label`}
              sx={{
                ...navTriggerLabelSx,
                color: tokens.theme.fg.action.onGhost,
              }}
            >
              {config.getItemName(selectedOption.item)}
            </Typography>
          </>
        ) : (
          <Typography
            variant="P14R"
            noWrap
            data-testid={`${config.testIdPrefix}-trigger-label`}
            sx={{
              ...navTriggerLabelSx,
              color: tokens.theme.fg.structural.secondary,
            }}
          >
            {config.defaultTriggerLabel}
          </Typography>
        )}

        <NavTriggerCaret />
      </Box>

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          marginTop: '4px',
          padding: '4px 0px',
        }}
        slotProps={{
          paper: {
            sx: { backgroundColor: tokens.theme.bg.structural.surface },
          },
        }}
        disableScrollLock
      >
        <Box minWidth={boxMinWidth} width={menuWidth}>
          <ListSearchInput
            autoFocus
            autoComplete="off"
            size="small"
            placeholder={config.searchPlaceholder}
            listItemProps={{
              sx: { '&.Mui-focusVisible': { background: 'none' } },
            }}
            value={searchInputValue}
            onChange={(_: unknown, v: string) => setSearchInputValue(v)}
            showClearButton
            clearButtonProps={{
              onClick: () => setSearchInputValue(''),
            }}
            disabled={isLoading || hasError || !options.length}
          />

          <List
            size={listSize}
            disablePadding
            testId={`${config.testIdPrefix}-list`}
          >
            {renderListContent()}
          </List>
        </Box>
      </Popover>
    </>
  );
};
